/**
 * 记一笔 —— 🔴 **P0 主操作**（`AC-O2`：≤3 步 / ≤5 秒）。
 *
 * ## 五种录入模式（🔴 2026-10-04 按「用户的账」重分组）
 *
 * | 模式 | 用户在想什么 | 提交出去的形态 | 腿数 |
 * |---|---|---|---|
 * | **支出** | "我花了一笔钱" | 1 腿 | 1 |
 * | **拆分** | "这笔钱花在了好几处" | N 腿（同账户同向） | N |
 * | **收入** | "我进了一笔钱" | 1 腿 | 1 |
 * | **工资条** | "这是我这个月工资，扣了这些" | N 腿（可跨账户 · 含不计收支腿） | N |
 * | **转账** | "我把钱从一个账户挪到另一个" | 2 腿（`out` + `in`）· 整笔不计收支 | 2 |
 *
 * 🔴 **为什么不再是「多条腿」一档**：那个词是**数据模型词**（`legs` 表），用户看不懂；
 * 而且它把**拆分（支出）与工资条（收入）两个毫不相干的场景**塞进同一档。
 * 现在按用户的心智分：**拆分本质是支出、工资条本质是收入** ⇒ 各自归到对应方向下。
 * 底层形态不变（拆分/工资条都落 `multi`），只是**给用户看的入口**变了。
 * ⚠️ 跨币种转账属**另一形态**（`DR-004` §2.5）⇒ 本页拦下并说明。
 *
 * ## 🔴 三条本页**不做**的判断（全部推给共享层内核）
 *
 * 腿数上限（按账户数分派 10 / 15 / 40）· 「资金出口恒为 1 个主账户」· 「全排除腿只允许 1~2 条」——
 * 这三条**唯一实现在 `@jiaying/shared` 的 `checkLegShape()`**，服务端写库前调**同一个**函数。
 * 本页只做两件事：把草稿喂给它、**把它的中文文案原样显示**。
 * ⇒ 端侧**一句形态规则都不重写**（铁律 24：防两处各答一遍）。
 *
 * ## ⚠️ 本页**不含**什么（诚实登记）
 * - ~~退款（冲减原消费）：不在本批~~ —— 🆕 **批 119/120/129 已落地**：入口是原交易行上的
 *   「退款」动作（`features/refund-entry.ts` 是入口判据的唯一实现），本页读 `history.state.refund`
 *   进入退款态。**不需要先在本页选一笔原交易**（那会变成第二个入口）。
 * - **跨币种转账**：拦下并说明（见 `txn-draft.ts` 的 P0 范围限制）。
 * - **账本时区**：日期默认取**浏览器本地日**（`toISOString` 在 UTC 下会错一天）；账本时区应来自
 *   `GET /ledgers` 的 `timezone`（已下发，本批仍未接）。
 * - **图标 / 自建分类 / 拼音检索**：随分类字形池与自定义分类批次。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountType,
  CAT,
  CategoryKind,
  ContraKind,
  LegDirection,
  MaxPayrollItems,
  MaxSplitChildren,
  SALARY_MODE_SIMPLE,
  TxnStatusFilter,
  centsToYuanText,
  currencySymbolOf,
  formatMoneyText,
  lineOriginal,
  // 🆕 批 116：首期账单日的提示值与服务端**同一个内核**（`DR-067` D2②）
  nextAfter,
  toCents,
  type AccountView,
  type CategoryView,
  type CreateTransactionRequest,
} from '@jiaying/shared';
import { Loading } from '../ui/Loading.js';
import { AccountPicker } from '../ui/AccountPicker.js';
import { CategoryPicker } from '../ui/CategoryPicker.js';
import { Grid } from '../ui/Grid.js';
import { SalarySheet } from '../ui/SalarySheet.js';
import {
  createTransaction,
  getLastSalary,
  getTransaction,
  listAccounts,
  listCategories,
  updateTransaction,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  applyMode,
  buildRequest,
  describeShape,
  editDraft,
  emptyLeg,
  initialDraft,
  isMultiMode,
  modeKind,
  refundDraft,
  // 🆕 批 129：剩余可退的**唯一实现**（横幅与金额预填共用，端侧不再各算一遍）
  refundLeftCents as refundLeftOf,
  resolveSplitDiscount,
  supportsSplit,
  yuanTextToCentsOrNull,
  type DraftLeg,
  type EntryMode,
  type RefundSource,
  type TxnDraft,
} from '../features/txn-draft.js';
// 🆕 批 141：工资条的「子项层」—— 界面只认「一项」，提交前由这里摊成腿（唯一展开处）
import {
  defaultSalaryItems,
  expandSalaryItems,
  mainAccountOfLegs,
  salaryItemsFromLegs,
  salaryTotals,
  validateSalaryItems,
  type SalaryItem,
} from '../features/salary-items.js';
import { localToday } from '../app/local-day.js';
import { dailyAccountsOf } from '../features/account-scope.js';
// 🆕 批 116：期数档位与手续费文案与**分期页同一份**（`DR-067` §5「别另造一套」）
import {
  INSTALLMENT_FEE_MODE_LABELS,
  INSTALLMENT_PERIOD_OPTIONS,
} from '../features/installment-options.js';
import { safeReturnTo } from '../features/return-to.js';

/**
 * 账户下拉的最小投影。
 *
 * 🔴 **候选范围不在这里决定** —— 由 `features/account-scope.ts` 的 `dailyAccountsOf()`
 * 一处判定（`DR-066` D1：日常账户 + 编辑态例外），本页只负责搬字段。
 *
 * 🆕 批 `116`：多带 `type` 与 `credit` —— 「记账时分期」的开关只在
 * **账户是信用卡**时出现（`DR-067` D2④），而首期账单日的提示要用 `credit.billingDay`。
 */
type Option = Pick<AccountView, 'id' | 'name' | 'ownerName' | 'currency' | 'type' | 'credit'>;

/** 账户视图 → 下拉投影（**唯一搬运处** —— 两处候选列表共用，别各写一份 `map`） */
function toOption(a: AccountView): Option {
  return {
    id: a.id,
    name: a.name,
    ownerName: a.ownerName,
    currency: a.currency,
    type: a.type,
    credit: a.credit,
  };
}

const amountOk = (text: string): boolean => /^\d+(\.\d{1,2})?$/.test(text.trim());

/**
 * 每种模式的**一句人话**（紧贴模式行显示）。
 *
 * 🔴 存在的理由：模式行下面直接就是表单，用户得先知道"这一档是干什么的"。
 * 原先靠一个「多条腿」+ 侧栏的"腿数上限 15 条"来解释 —— 那是**实现细节当说明**，
 * 用户不知道什么叫腿。改成用**场景**说话（"这笔钱花在了好几处"）。
 *
 * ⚠️ 文案里**不出现「腿」**：腿是 `legs` 表的概念，只该出现在开发文档里。
 * 条数上限那条**转移到了多笔区块的超限提示**（由内核文案给出），
 * 平时不展示 —— 正常用户不会记到 10 条以上。
 */
const MODE_HINT: Record<EntryMode, string> = {
  out: '一笔日常开销：选账户 → 填金额 → 选分类',
  in: '一笔进账：选账户 → 填金额 → 选分类',
  transfer: '把钱从一个账户挪到另一个（不计收支，两边余额同时变）',
  salary: '这个月的工资：实发到账，同时记下扣掉的个税 / 社保 / 公积金',
};

/** 「拆分」开关打开后的补充说明（紧贴开关，替代单独一行提示） */
const SPLIT_HINT: Record<'out' | 'in', string> = {
  out: '这笔钱花在了好几处，想分开归到不同分类（如超市 100 元：食材 60 + 日用品 40）',
  in: '这笔钱来自好几处（如一笔退款分别冲减两类支出），想分开归到不同分类',
};

/**
 * 紧凑分类下拉（**多笔模式每笔一个**）。
 *
 * 🔴 2026-10-04 起**与单笔共用同一个 `CategoryPicker`** —— 原先这里有一份
 * 独立的 `CategorySelect`（MUI `Select`，只有视觉缩进、**不能搜索**）。负责人要求
 * 「分类也需要优化，给个下拉列表，把一级和二级按照缩进编排」+「模糊搜索对应类别和账户」，
 * 两条需求落在同一个组件上 ⇒ 两份实现合并成一份（也顺带消掉 `AC-B7.9`
 * 「无遗漏、**无第二套实现**」的一处隐患）。
 *
 * 数据源仍是 `GET /api/categories`（**唯一的分类清单**），不硬编任何分类。
 */

/**
 * 一笔的编辑行（多笔模式：**拆分 / 工资条**）。
 *
 * ## 🔴 两种形态 —— 因为这两个场景的字段**本来就不同**（`DR-043`）
 *
 * | 形态 | 列 | 依据 |
 * |---|---|---|
 * | **拆分**（收 / 支内的开关 · `isSplit`） | **分类 · 金额 · 优惠 · 备注**（+ 删除）—— **一行** | `DR-040` D7「账户 / 方向 / 日期 / 整单优惠 = 父级，子笔 = 分类 / 原价 / 优惠 / 备注」+ 负责人 2026-10-14「每一行需要分类，金额，优惠金额，备注」「每一项只要一行」 |
 * | **工资条**（`salary`） | 方向 · 账户 · 金额 · 分类 · 不计收支（多行卡片） | `DR-016` §3.3：工资条**天然跨账户、有收有付**，且含「转账性质的子项」⇒ 账户与方向**必须逐项可选** |
 *
 * ## 🔴 拆分行「三不给」（都是**不给犯错的机会**，而不是靠校验兜）
 * 1. **不给账户** —— 账户已**提升为父级**（`DR-040` D7）⇒「拆分 = 同账户同向」是**结构保证**；
 *    同时消除「逐行改账户 ⇒ 形态静默变成工资条」那条路径（`DR-040` 不一致 #2）。
 * 2. **不给方向** —— 锁死 = 父方向（`lockDirection`，负责人 2026-10-04「必须是一个方向」）。
 * 3. **不给「不计收支」** —— 那是工资条独有的语义（社保 / 公积金转账子项）；`DR-040` D7 的字段矩阵里没有它。
 *
 * ## 🔴 「金额」填的是**实付净额**
 * 优惠 = **减免**（`DR-040` D3：钱当场少花、从未流出）⇒ 契约 `amount` 恒为实付。
 * **原价 = 金额 + 优惠**（派生、不落库）—— 行内把它算出来给用户对一眼，但不提交。
 */
function LegRow({
  index,
  leg,
  accounts,
  items,
  removable,
  lockDirection,
  isSplit,
  wholeShare,
  onChange,
  onRemove,
}: {
  index: number;
  leg: DraftLeg;
  accounts: readonly Option[];
  items: readonly CategoryView[];
  removable: boolean;
  /**
   * 🔴 **锁死方向**（拆分用）—— 负责人 2026-10-04：
   * 「必须是一个方向，要么支出，要么收入，因为父项在银行对账单就一笔」。
   * 打开时**不渲染方向开关**，方向由 `applyMode` 统一对齐成父方向 ⇒ 不给犯错的机会。
   * 工资条传 `false`（它天然有收有付，必须能逐笔选方向）。
   */
  lockDirection: boolean;
  /** 🆕 拆分模式 ⇒ **紧凑单行**（无账户 / 无方向 / 无「不计收支」）—— 见上方表格 */
  isSplit: boolean;
  /**
   * 🆕 **本行分摊到的整单优惠**（整数分，批次 `94`）。
   *
   * 🔴 只读展示（`+3.20` 尾巴）—— 具体数字由调用方经 `resolveSplitDiscount` 一次算好，
   * 本组件**不参与取整**（共享层是唯一实现，铁律 24）。
   */
  wholeShare?: number;
  onChange: (patch: Partial<DraftLeg>) => void;
  onRemove: () => void;
}) {
  const cur = accounts.find((a) => a.id === leg.accountId)?.currency ?? '';
  const sym = cur === '' ? '' : currencySymbolOf(cur);
  const isExpenseLeg = leg.direction === LegDirection.Out;
  const discText = (leg.discountText ?? '').trim();
  const discBad = discText !== '' && !amountOk(discText);
  /**
   * **原价 = 实付 + 自填优惠 + 整单分摊**（派生、不落库）。
   *
   * 🔴 走整数分（`yuanTextToCentsOrNull` + `centsToYuanText`）而不是 `Number(...) + toFixed`：
   * 金额是**定点**场景，浮点加法在 .1 + .2 这类输入上会出现 0.30000000000000004 的尾巴，
   * 而这里显示的是"用户拿去核对的价格"（规格 §22.1）。
   *
   * ⚠️ 批 `117` 起**只有工资条那一支**用它（多行卡片，有 helperText 空间）；
   * **拆分是紧凑单行**，helperText 会把行撑高、把同行其它格挤歪 ⇒ 拆分行的原价一律不显示，
   * 公式只在区块级提示里讲（那里的原价也是"只作参考、不参与记账"）。
   */
  const originalText = (() => {
    if (discBad) return null;
    const amountCents = yuanTextToCentsOrNull(leg.amountText);
    if (amountCents === null || amountCents <= 0) return null;
    const selfCents = discText === '' ? 0 : yuanTextToCentsOrNull(discText);
    if (selfCents === null || selfCents < 0) return null;
    return centsToYuanText(toCents(amountCents + selfCents + (wholeShare ?? 0)));
  })();

  // ── 拆分：**一行 4 列**（分类 / 金额 / 优惠 / 备注 + 删除）──
  if (isSplit) {
    return (
      <Box
        data-testid={`leg-row-${String(index)}`}
        sx={{
          display: 'grid',
          // 宽屏一行到底；窄屏折成两列（分类与备注各占满一行）——
          // 🔴 不用固定像素宽度凑，避免横向溢出（断点 720 见铁律 36）
          gridTemplateColumns: isExpenseLeg
            ? {
                xs: 'repeat(2, minmax(0, 1fr)) 32px',
                md: 'minmax(0, 1.3fr) 104px 104px minmax(0, 1fr) 32px',
              }
            : {
                xs: 'repeat(2, minmax(0, 1fr)) 32px',
                md: 'minmax(0, 1.3fr) 104px minmax(0, 1fr) 32px',
              },
          gap: 0.75,
          alignItems: 'center',
        }}
      >
        <Box sx={{ gridColumn: { xs: '1 / 3', md: 'auto' } }}>
          <CategoryPicker
            label="分类"
            items={items}
            kind={isExpenseLeg ? CategoryKind.Expense : CategoryKind.Income}
            value={leg.categoryId}
            onChange={(v) => onChange({ categoryId: v })}
            // 🔴 说明只给一次（区块级），行内重复会把「一行」撑成两行
            showHint={false}
          />
        </Box>

        {/*
          🔴 批 `117`：这一格叫「**实付**」，不叫「金额」。
          修前 label 写「金额」、而旁边那格的「备注」placeholder 又灰着「原价 65」——
          用户很自然地把**账单上的原价**填进这一格，于是 65 + 优惠 5 被记成 65（优惠前金额入账），
          而不是实付 60。`DR-040` D3 的口径没变：`amount` 恒为**实付净额**、原价 = `amount + discount`
          是派生量、不入账 ⇒ 界面必须让"填进去的就是入账的那个数"一眼可见。
        */}
        <TextField
          size="small"
          required
          inputMode="decimal"
          label={sym === '' ? '实付' : `实付（${sym}）`}
          placeholder="0.00"
          value={leg.amountText}
          onChange={(e) => onChange({ amountText: e.target.value })}
          error={leg.amountText !== '' && !amountOk(leg.amountText)}
          inputProps={{ 'data-testid': `leg-amount-${String(index)}` }}
        />

        {/* 🔴 优惠**只有支出笔才有语义**（`AC-C2.34`：`in` 腿传非 0 服务端拒绝）——
            收入时**不渲染这一格**（不给犯错的机会），而不是渲染出来再拦 */}
        {isExpenseLeg && (
          <TextField
            size="small"
            inputMode="decimal"
            label="优惠"
            placeholder="0.00"
            value={leg.discountText ?? ''}
            onChange={(e) => onChange({ discountText: e.target.value })}
            error={discBad}
            helperText={discBad ? '最多两位小数' : undefined}
            /*
              🆕 批次 `94`：本行**分摊到的整单优惠**（只读尾巴，`+3.20`）。
              🔴 数字来自 `resolveSplitDiscount`（共享层取整的唯一实现），此处**不参与计算**。
              ⚠️ 用 `endAdornment` 而不是 helperText：helperText 会把这一行撑高、把同行其它格挤歪。
            */
            slotProps={{
              htmlInput: { 'data-testid': `leg-discount-${String(index)}` },
              input:
                wholeShare !== undefined && wholeShare > 0
                  ? {
                      endAdornment: (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'nowrap' }}
                          data-testid={`leg-whole-share-${String(index)}`}
                        >
                          +{centsToYuanText(toCents(wholeShare))}
                        </Typography>
                      ),
                    }
                  : undefined,
            }}
          />
        )}

        {/*
          🔴 批 `117`：placeholder **不再显示「原价 xx」**。
          它是"金额栏该填原价"的最大误导源（用户看到灰字「原价 65」，就把 65 填进金额格）。
          原价是派生量、不入账 ⇒ 只在区块级说明里讲清公式，行内不给任何"原价"数字。
        */}
        <TextField
          size="small"
          label="备注"
          placeholder="可不填"
          value={leg.note ?? ''}
          onChange={(e) => onChange({ note: e.target.value })}
          inputProps={{ 'data-testid': `leg-note-${String(index)}` }}
          sx={{ gridColumn: { xs: '1 / 3', md: 'auto' } }}
        />

        {removable && (
          <IconButton
            size="small"
            aria-label={`删除第 ${String(index + 1)} 笔`}
            onClick={onRemove}
            sx={{ justifySelf: { xs: 'end', md: 'center' } }}
          >
            ✕
          </IconButton>
        )}
      </Box>
    );
  }

  // ── 工资条：多行卡片（账户 / 方向**必须逐项可选**，见文件头表格）──
  return (
    <Stack
      spacing={1.5}
      sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {/* 🔴 用户看到的是「第 N 笔」而不是「第 N 条腿」—— 腿是 `legs` 表的概念 */}
          第 {String(index + 1)} 笔
        </Typography>
        {lockDirection ? (
          // 拆分：方向锁死，只**显示**不提供切换（改方向请切上面的模式）
          <Typography variant="caption" color="text.secondary" data-testid={`leg-dir-locked-${String(index)}`}>
            {leg.direction === LegDirection.In ? '收入' : '支出'}
          </Typography>
        ) : (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={leg.direction}
            onChange={(_e, v: string | null) => {
              if (v === null) return;
              // 方向变了 ⇒ 清分类（分类按 kind 分家，留着会指向另一方向的分类）
              onChange({ direction: v as DraftLeg['direction'], categoryId: null });
            }}
          >
            <ToggleButton value={LegDirection.Out}>支出</ToggleButton>
            <ToggleButton value={LegDirection.In}>收入</ToggleButton>
          </ToggleButtonGroup>
        )}
        {removable && (
          <IconButton size="small" aria-label={`删除第 ${String(index + 1)} 笔`} onClick={onRemove}>
            ✕
          </IconButton>
        )}
      </Stack>

      <AccountPicker
        label="账户"
        accounts={accounts}
        value={leg.accountId}
        onChange={(id) => {
          onChange({ accountId: id });
        }}
      />

      <TextField
        size="small"
        required
        inputMode="decimal"
        label={sym === '' ? '金额' : `金额（${sym}）`}
        placeholder="0.00"
        value={leg.amountText}
        onChange={(e) => onChange({ amountText: e.target.value })}
        error={leg.amountText !== '' && !amountOk(leg.amountText)}
        helperText={
          leg.amountText !== '' && !amountOk(leg.amountText) ? '最多两位小数' : undefined
        }
      />

      {/*
        🆕 批次 `94`：**单笔也有优惠**（`DR-040` 语义不变 —— 减免、钱当场没流出、`amount` 恒为实付）。

        🔴 修前只有「拆分」模式有这一格：普通一笔支出（满减 / 立减 / 抹零）根本没有地方填，
        用户只能把"少花的钱"丢掉或自己心算原价。

        🔴 **收入侧不渲染**（`AC-C2.34`：`in` 腿传非 0 服务端直接拒）——
        不给犯错的机会，而不是渲染出来再拦。
      */}
      {isExpenseLeg && (
        <TextField
          size="small"
          inputMode="decimal"
          label={sym === '' ? '优惠（选填）' : `优惠（${sym}，选填）`}
          placeholder="0.00"
          value={leg.discountText ?? ''}
          onChange={(e) => onChange({ discountText: e.target.value })}
          error={discBad}
          helperText={
            discBad
              ? '最多两位小数'
              : originalText === null
                ? '按实付记；有满减 / 抹零就填这里'
                : `原价 ${originalText}（优惠只作记录，不入账）`
          }
          inputProps={{ 'data-testid': `leg-discount-${String(index)}` }}
        />
      )}

      {/* 分类按**该笔自己的方向**取列表（工资条里收入/支出笔混排，不能一刀切） */}
      <CategoryPicker
        label="分类（可留空）"
        items={items}
        kind={leg.direction === LegDirection.Out ? CategoryKind.Expense : CategoryKind.Income}
        value={leg.categoryId}
        onChange={(v) => onChange({ categoryId: v })}
      />

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={leg.excludeFromStats ?? false}
            onChange={(e) => onChange({ excludeFromStats: e.target.checked })}
          />
        }
        label="本条不计收支（如工资条里的社保 / 公积金转账子项）"
      />
    </Stack>
  );
}

export function NewTransactionPage() {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  // 🔴 分类**一次取全量**（两个方向都在手）—— 切换方向不重取，见 `endpoints.ts`
  const cats = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  /**
   * 🔴 **退款专线入口**（`DR-035` v1.1 · C 域 PRD §5.2.3「前端引导」）。
   *
   * 入口 = 首页 / 流水页 / 详情抽屉里某笔支出上的「退款」动作
   * ⇒ `navigate('/new', { state: { refund } })`，本页读 `history.state.usr.refund`。
   *
   * ⚠️ **不是 query string**（批 129 订正了这里的旧注释：它曾写 `/new?refundOf=<id>`，
   * 与实现不符）。用 history state 的理由：原备注会进 URL ⇒ 长度限制 + 备注出现在浏览历史里。
   *
   * ⚠️ 本页**不去查原交易**：跳转方把账户/备注/原额/已退额一并递过来（少一次请求，
   * 也避免"查不到就渲染成普通记账"的静默降级）。
   * 拿不到 state（比如用户直接改地址栏）⇒ 退化为**普通记账**，不假装是退款。
   */
  const [refund] = useState<RefundSource | null>(() => {
    // 🔴 必须安全访问：静态渲染（`renderToStaticMarkup`）与任何非浏览器环境下
    //    **没有 `window`** —— 直接写 `window.history.state` 会抛 `ReferenceError`，
    //    而 React 的 `useState` 初始化函数里抛错会把整页干掉（不是"降级为普通记账"）。
    const st = (globalThis as { history?: { state?: unknown } }).history?.state as
      | { usr?: { refund?: RefundSource } }
      | undefined;
    return st?.usr?.refund ?? null;
  });

  const [mode, setMode] = useState<EntryMode>(refund === null ? 'out' : 'in');
  /**
   * 「拆分」开关（**只对收 / 支有意义**）。
   *
   * 🔴 它是**从属于方向**的状态，不是第四种模式 —— 切走模式时由 `onModeChange` 归位。
   * 与 `mode` 一起决定提交形态：`out` + 开 = `multi`（拆分），`out` + 关 = `single`。
   */
  const [split, setSplit] = useState(false);
  /**
   * 🆕 批次 95：`/new?account=<id>` —— 从「账户」页某个账户直接记一笔时**预选该账户**。
   * 🔴 必须在草稿初值之前取（`useState` 的初始化只跑一次，晚拿就晚了）。
   */
  const [search] = useSearchParams();
  const presetAccount = search.get('account') ?? '';
  const [draft, setDraft] = useState<TxnDraft>(() =>
    refund === null ? initialDraft(localToday(), presetAccount) : refundDraft(refund, localToday()),
  );
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [ok, setOk] = useState<string | null>(null);
  /**
   * 🔴 **编辑一笔已删除的流水** ⇒ 服务端 `DELETED`（**HTTP 410** · 架构 §6.5「删除优先」）
   * ⇒ 端侧必须给出「先去回收站恢复」的引导（`AC-C1.46`）—— 只回一句红字，用户不知道怎么继续。
   */
  const [deletedConflict, setDeletedConflict] = useState(false);

  /**
   * 🆕 批 `141` · 工资条的**子项草稿**（`DR-016` §3.8）。
   *
   * `null` = 当前不用清单界面，有两种情况：
   * ① 还没进过「工资条」这一档；② 编辑一笔历史工资条、但**折叠失败**
   *    （腿结构与子项对不上）⇒ 回退到原来的逐腿编辑，**不猜、不丢钱**。
   */
  const [salaryItems, setSalaryItems] = useState<SalaryItem[] | null>(null);
  /**
   * 「银行到账多少」—— 🔴 **只用于录入期对账，不落库、不参与账务**
   * （`DR-016` §3.8⑤ / C 域 PRD §5.2.1⑩）。
   */
  const [salaryNetText, setSalaryNetText] = useState('');
  /**
   * 工资到账的账户（**资金出口恒为 1 个** · `DR-016` §3.8③）。
   * 空串 = 还没选过 ⇒ 回落到第一个日常账户（减少一次点击）。
   */
  const [salaryMainPicked, setSalaryMainPicked] = useState('');
  /** 🆕 批 141：「沿用上月」的取数中状态（防连点） */
  const [salaryCopyBusy, setSalaryCopyBusy] = useState(false);

  // ─────────── 🆕 编辑路径（`DR-042` D1 · `AC-C1.41`）───────────

  const navigate = useNavigate();
  /**
   * 🔴 **编辑入口**：`/new?editOf=<id>` —— 由详情抽屉的「编辑」按钮生成（`AC-C1.41`）。
   * 走**查询参数**而不是 `history.state`：URL 自解释、可刷新、可从任何入口进；
   * 退款走 state 是因为它带的是一个**对象**（原交易摘要），而编辑只需要一个 id（详情自己去查）。
   */
  const editOf = search.get('editOf');
  const editing = editOf !== null && editOf !== '';
  /**
   * 🆕 批 `115`（`DR-066` §5 · `AC-B5.10`）：**保存后的去向**。
   *
   * 🔴 只算一次，横幅文案与 `navigate()` 读**同一个值** —— 否则会出现
   * "横幅说回流水列表、实际跳回账单页"这种自相矛盾的提示。
   * 白名单（`/journal` 或 `/accounts/<id>/bill`）在 `features/return-to.ts`，
   * 别处**禁止**再解释一遍 `returnTo`（防开放重定向的口径只有一处）。
   */
  const returnTo = safeReturnTo(search.get('returnTo'));

  const detail = useQuery({
    queryKey: ['transaction', editOf ?? ''],
    queryFn: () => getTransaction(editOf as string),
    enabled: editing,
  });

  /**
   * 🔴 **只预填一次** —— 用 `useRef` 做标记而**不是** state：
   * ① 标记不该触发重渲染；② 若用 state，effect 依赖变化会在用户**已经改过字段之后**再覆盖一次
   *    （表现 = "我改的又被冲掉了"）。真正的防线是这把一次性锁。
   */
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editing || prefilled.current || detail.data === undefined) return;
    const r = editDraft(detail.data);
    setDraft(r.draft);
    setMode(r.mode);
    setSplit(r.split);
    /**
     * 🆕 批 `141`：编辑一笔工资条时，把腿**折回子项**（一项一行）。
     *
     * 🔴 折不了（`null`）就**保持 null** ⇒ 页面渲染原来的逐腿编辑。
     * 这是刻意的退路：折叠算法只要能丢一条腿，就不该被启用
     * （`legs` 是整组替换，「原样保存」会把丢的那条删掉）。
     */
    if (r.mode === 'salary') {
      setSalaryItems(
        salaryItemsFromLegs(detail.data.legs, {
          accounts: accounts.data?.items ?? [],
          categories: cats.data?.items ?? [],
        }),
      );
      setSalaryMainPicked(mainAccountOfLegs(detail.data.legs) ?? '');
    } else {
      setSalaryItems(null);
    }
    // 编辑态下"已填过"提示不必再出现（内容是从库里来的，不是用户刚输的）
    setOk(null);
    prefilled.current = true;
  }, [editing, detail.data]);

  const save = useMutation({
    /**
     * 🔴 **同一个入口两件事**（`DR-042` D1）：
     * `id === null` ⇒ 新建（`POST`）；否则 ⇒ **改一笔**（`PATCH`）。
     * 两者**共用 `buildRequest` 的结果** ⇒ 不存在"编辑走另一套拼装"的第二份实现。
     */
    mutationFn: (v: { id: string | null; req: CreateTransactionRequest }) =>
      v.id === null ? createTransaction(v.req) : updateTransaction(v.id, v.req),
    onSuccess: (_row, v) => {
      setErrors([]);
      // 余额与最近记录都变了 —— 让相关查询失效（缓存失效交给 Query，不由页面手工重取）
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      // 🆕 批 116：记账时分期 ⇒ 分期列表与卡面「本期分期应还」都变了（同一 key 全家）
      if (v.req.installment !== undefined) {
        void qc.invalidateQueries({ queryKey: ['installments'] });
      }
      if (v.id !== null) {
        // 🔴 编辑保存 ⇒ **回原地并刷新那一行**（`AC-C1.41`：返回列表、不整页重载）
        //
        // 🆕 批 `115`（`DR-066` §5 · `AC-B5.10`）：去向由 `?returnTo=` 决定，
        //    经**白名单**归一（`features/return-to.ts`）—— 缺省 / 非法一律落 `/journal`，
        //    所以"没传 returnTo"时的行为与修前**逐字节一致**（零回归）。
        void qc.invalidateQueries({ queryKey: ['transaction', v.id] });
        navigate(returnTo);
        return;
      }
      setOk('已记录');
      setDraft((d) =>
        d.form === 'transfer'
          ? { ...d, transferAmountText: '' }
          : { ...d, legs: d.legs.map((l) => ({ ...l, amountText: '' })) },
      );
    },
    onError: (e) => {
      setOk(null);
      // 🔴 已删除 ⇒ 置引导标志（`AC-C1.46`）；其余错误原样展示服务端 message
      setDeletedConflict(
        e instanceof ApiError && (e.status === 410 || e.body.code === 'DELETED'),
      );
      // 🔴 服务端 message 是给人看的中文（含"为什么"），原样展示
      setErrors([e instanceof ApiError ? e.message : String(e)]);
    },
  });

  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <Alert severity="error">账户读取失败：{String(accounts.error)}</Alert>;
  // 🔴 编辑态：详情没到位**不渲染表单** —— 否则用户看到一张空表单，会以为"原来的数据丢了"
  if (editing && detail.isPending) return <Loading />;
  if (editing && detail.isError) {
    return (
      <Alert severity="error" data-testid="edit-load-error">
        这笔流水读取失败：{detail.error instanceof ApiError ? detail.error.message : String(detail.error)}
      </Alert>
    );
  }

  /**
   * 🔴 **记账可选账户 = 日常账户 ∪ 当前草稿里已出现的账户**（`DR-066` D1 · `AC-B5.1` / `AC-B5.2`）。
   *
   * 修前这里只写了一句 `.filter((a) => !a.hidden)`，而服务端返的是**全量账户** ⇒
   * 借贷往来 / 贷款 / 投资 / 社保品类全挤在"记一笔"的下拉里（负责人 2026-10-28 第 2 条）。
   *
   * | 部分 | 作用 |
   * |---|---|
   * | `dailyAccountsOf(...)` | **唯一实现**在 `features/account-scope.ts`（别处禁再写 `type === 'contra'`） |
   * | `draftAccountIds` | 🔴 **编辑态例外**：编辑一笔挂在贷款 / 往来账户上的历史交易时，那个账户必须在候选里 —— 否则选择器找不到值 ⇒ 显示空白 ⇒ 用户以为"账户丢了" |
   *
   * ⚠️ 两处（拆分 / 工资条的每笔行、转账两端、单笔）**共用这一个数组** ⇒ 代码里只有这一处过滤。
   */
  const draftAccountIds = new Set(
    [...draft.legs.map((l) => l.accountId), draft.fromAccountId, draft.toAccountId].filter(
      (id) => id !== '',
    ),
  );
  const dailyIds = new Set(dailyAccountsOf(accounts.data.items).map((a) => a.id));
  const options: Option[] = accounts.data.items
    .filter((a) => dailyIds.has(a.id) || draftAccountIds.has(a.id))
    .map(toOption);
  const catItems = cats.data?.items ?? [];

  /**
   * 🆕 批 `141` · 工资条的两个账户池（**范围不同，不能共用一个**）。
   *
   * | 池 | 范围 | 为什么 |
   * |---|---|---|
   * | `dailyOptions`（到账账户） | **只日常账户** | 工资卡是日常账户；`DR-066` D1 的同一判据 |
   * | `allOptions`（转账目标） | **全量账户** | 社保 / 公积金 / 贷款 / 投资都在别的品类里 —— 工资条的转账子项**必然**跨到这些账户（`DR-016` §3.3） |
   *
   * ⚠️ 用 `options`（日常 ∪ 草稿里出现过的）当转账目标会漏掉"还没被选过的社保账户"，
   * 那样医保 / 公积金就选不到自己的账户 —— 这正是改前"要手录两条腿"的原因之一。
   */
  const dailyOptions: Option[] = accounts.data.items
    .filter((a) => dailyIds.has(a.id))
    .map(toOption);
  const allOptions: Option[] = accounts.data.items.map(toOption);

  /**
   * 工资条的到账账户（资金出口）：用户选过的 > 编辑态折叠出的 > 第一个日常账户。
   *
   * 🔴 优先用**折叠出的那个**：历史工资条的主账户是既有事实，不能因为"排序第一个"
   * 就被悄悄换掉（换掉 = 整笔的账户全变）。
   */
  const foldMainAccount = editing && detail.data !== undefined ? mainAccountOfLegs(detail.data.legs) : null;
  const salaryMain =
    salaryMainPicked !== ''
      ? salaryMainPicked
      : foldMainAccount !== null && foldMainAccount !== ''
        ? foldMainAccount
        : (dailyOptions[0]?.id ?? '');

  /** 工资条当前会展开成什么腿 —— 提交、口径说明、超限校验都读**这一个**结果 */
  const salaryLegs =
    mode === 'salary' && salaryItems !== null ? expandSalaryItems(salaryItems, salaryMain) : null;
  /**
   * 🆕 「垫付到…」的可选账户（`DR-048` D7）。
   *
   * 🔴 **只收 `advance` / `reimburse` 两类**：应收 / 应付 / 贷款不是"垫出去等报回来"的关系
   * （借出钱是 `receivable`，垫付是 `advance` / `reimburse`）。
   * ⚠️ 这里**不过滤 hidden**：隐藏的往来账户仍可被选中（隐藏只是"不在净资产里显示"）。
   */
  const advanceOptions: Option[] = accounts.data.items
    .filter((a) => a.type === AccountType.Contra
      && (a.contraKind === ContraKind.Advance || a.contraKind === ContraKind.Reimburse))
    .map(toOption);

  // 🔴 金额的币种 = **账户的币种**（不是写死的"元"）—— 选到美元账户时标签就是「金额（$）」
  const singleAcc = draft.legs[0]?.accountId ?? '';
  /** 🆕 批 `120`（`DR-069` D6）：退款行的币种 —— 「最多可退」要用 `formatMoneyText`（符号单源） */
  const singleCurrency = options.find((a) => a.id === singleAcc)?.currency ?? '';
  /**
   * 🆕 批 `120`（`DR-069` D6）：退款页的「最多可退」= |原额| − 已退。
   *
   * 🔴 修前 `RefundSource.net` 的注释就写着"用于展示最多可退参考值"，但**全仓没有一处渲染它**
   * （铁律 53：没入口 = 没兑现）—— 部分退款后再退，用户只能靠心算。
   */
  const refundOriginalCents = refund === null ? 0 : Math.abs(refund.net ?? 0);
  /**
   * 🆕 批 `129`：改走 `refundLeftCents()` —— 与**金额栏默认值**同一个函数。
   * 修前这里是页面内联的一行减法，而预填逻辑在 `refundDraft()` 里没有 ⇒ 两个数各算一遍，
   * 一旦口径变化（比如以后要扣手续费）就会出现"横幅说最多退 50、输入框填了 48"。
   */
  const refundLeftCents = refund === null ? 0 : refundLeftOf(refund);
  const singleSym = (() => {
    const c = options.find((a) => a.id === singleAcc)?.currency ?? '';
    return c === '' ? '' : currencySymbolOf(c);
  })();
  const transferFrom = options.find((a) => a.id === draft.fromAccountId);
  const transferSym = transferFrom === undefined ? '' : currencySymbolOf(transferFrom.currency);

  /**
   * 🆕 **整单优惠的实时分摊结果**（批次 `94`）—— 界面显示各行 `+x.xx` 与提交落库
   * 都读它一个来源（`buildRequest` 内部调的是同一个函数）。
   *
   * 🔴 每帧重算（纯函数、无 IO、几行分）—— 不缓存、不进 state：
   * 缓存会引入"输入变了但分摊没跟上"的陈旧态，那正是本批要消掉的一类问题。
   */
  const wholeResolved = resolveSplitDiscount(draft.legs, draft.splitDiscountText ?? '');
  const splitAccCur = options.find((a) => a.id === (draft.legs[0]?.accountId ?? ''))?.currency ?? '';
  const splitSym = splitAccCur === '' ? '' : currencySymbolOf(splitAccCur);

  /**
   * 🆕 批 `115` · T8：**单笔支出的优惠**（`AC-B5.13`）。
   *
   * ## 🔴 为什么这一支原来是空的
   *
   * 内核早就支持（`buildRequest` 的 `resolveSplitDiscount` 对单腿同样成立），
   * 拆分行与工资条行也有输入框 —— **唯独单笔支出那一支漏了入口**（铁律 53：没入口 = 没兑现）。
   * 用户满减 / 立减 / 抹零时只能把"少花的钱"丢掉，或者自己心算原价。
   *
   * ## 🔴 原价必须走 `lineOriginal`（共享层唯一实现）
   *
   * 拆分那边显示原价也是同一条公式（`实付 + 优惠`）。在这里再写一次 `+` 就是第二份实现 ——
   * 而"卡面 120 / 库里 121"这类偏差只能靠对账发现（铁律 24）。
   * 优惠额本身来自 `resolveSplitDiscount([leg], '')`（与拆分**同一个**分摊内核，此处分摊恒 0）。
   */
  const singleLeg = draft.legs[0];
  const singleResolved = resolveSplitDiscount(singleLeg === undefined ? [] : [singleLeg], '');
  const singleDiscountText = singleLeg?.discountText ?? '';
  const singleDiscBad = singleDiscountText.trim() !== '' && !amountOk(singleDiscountText);
  /** 这一笔的**入账金额**（= 金额栏那个数；批 `117` 起 helperText 要把它明写出来） */
  const singlePaidCents = yuanTextToCentsOrNull(singleLeg?.amountText ?? '');
  const singleOriginalCents: number | null = (() => {
    if (singleDiscBad) return null;
    const amount = yuanTextToCentsOrNull(singleLeg?.amountText ?? '');
    const disc = singleResolved.finals[0] ?? 0;
    if (amount === null || amount <= 0 || disc < 0) return null;
    return lineOriginal({ amount: toCents(amount), selfDiscount: toCents(disc) }) as number;
  })();

  /**
   * 🆕 批 `116`（`DR-067`）：**记账时分期**。
   *
   * ## 🔴 什么时候出现（三条同时成立）
   *
   * | 条件 | 依据 |
   * |---|---|
   * | `mode === 'out' && !split`（单笔支出） | 收入 / 转账 / 拆分 / 工资条都没有"这笔刷了多少钱"这个语义 |
   * | 账户是**信用卡**（`type === 'credit'`） | 只有信用卡有账期；花呗 / 白条不在本模型（`DR-067` §6 不做） |
   * | **非编辑态** | 改一笔已分期的消费金额会与计划不一致 ⇒ 编辑态一律不显示（改动请到分期页） |
   *
   * ## 🔴 首期账单日为什么端侧能显示
   *
   * 因为它调的是**同一个内核** `nextAfter()`（服务端算同一个值），不是第二份实现。
   * 账单日当天的消费计入下一期 —— 这与 `computeCreditBill` 的还款日推算是同一语义。
   * 显示的只是"服务端将这么算"，最终值仍由服务端定（`DR-067` D2②）。
   */
  const singleOption = options.find((a) => a.id === singleAcc);
  const singleIsCredit = singleOption?.type === AccountType.Credit;
  const singleBillingDay = singleOption?.credit?.billingDay ?? null;
  const showInstallment = mode === 'out' && !split && !editing && singleIsCredit;
  const installmentOn = draft.installmentPeriods !== undefined;
  /** 首期账单日提示值（拿不到账单日 ⇒ `null`，文案退化为"由服务端确定"） */
  const startBillDayHint =
    singleBillingDay === null ? null : nextAfter(draft.date, singleBillingDay);
  /** 提示里要显示的本金（= 本笔金额；还没填 / 不合法 ⇒ `null`，不显示 `¥0.00`） */
  const installmentAmountCents = (() => {
    const cents = yuanTextToCentsOrNull(draft.legs[0]?.amountText ?? '');
    return cents === null || cents <= 0 ? null : cents;
  })();

  /**
   * 开 / 关分期。
   *
   * 🔴 关掉时**清空三个字段**（而不是留一个"看不见但会提交"的值）——
   * 与 `applyMode` 的清理是同一条纪律。
   */
  const onInstallmentChange = (on: boolean): void =>
    setDraft((d) =>
      on
        ? {
            ...d,
            installmentPeriods: d.installmentPeriods ?? 12,
            installmentFeeMode: d.installmentFeeMode ?? 'total',
            installmentFeeText: d.installmentFeeText ?? '',
          }
        : {
            ...d,
            installmentPeriods: undefined,
            installmentFeeMode: undefined,
            installmentFeeText: undefined,
          },
    );

  const patchLeg = (i: number, patch: Partial<DraftLeg>): void =>
    setDraft((d) => ({ ...d, legs: d.legs.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  /**
   * 加一笔。🔴 新项的**方向跟随首笔**（`...emptyLeg()` 的默认是 `Out`）。
   *
   * 理由：**拆分**天生要求「同账户同向」（多笔支出），跟随首笔能让"加第二笔"直接可用；
   * **工资条**里扣款也是流出，所以默认 `Out` 同样合适；真要记"补贴"这种流入项，
   * 用该行上的方向开关切一下即可（方向切换会自动清分类，见 `LegRow`）。
   */
  const addLeg = (): void =>
    setDraft((d) => ({
      ...d,
      legs: [
        ...d.legs,
        { ...emptyLeg(d.legs[0]?.accountId ?? ''), direction: d.legs[0]?.direction ?? LegDirection.Out },
      ],
    }));
  const removeLeg = (i: number): void =>
    setDraft((d) => ({ ...d, legs: d.legs.filter((_l, idx) => idx !== i) }));

  /**
   * 🆕 🔴 **拆分：账户提升为父级**（`DR-040` D7）—— 改父级账户 = **同步全部子笔**。
   *
   * 子笔行**不渲染账户**（见 `LegRow`）⇒ 从结构上不可能出现"逐行账户不一致"。
   * 这条同时兑现负责人的需求 4（「修改拆分收支的父项时，同时修改所有子项，比如支出账户等等」）——
   * 它是**结构性保证**，不是"改一处再联动 N 处"的同步逻辑（与 `lockDirection` 同手法）。
   */
  const setSplitAccount = (id: string): void =>
    setDraft((d) => ({ ...d, legs: d.legs.map((l) => ({ ...l, accountId: id })) }));

  /** 多笔上限按**当前形态**取（共享层常量，端侧禁自算档位 —— 规格 §3.5） */
  const legCap = mode === 'salary' ? MaxPayrollItems : MaxSplitChildren;

  /** 切模式：拆分开关**归位**（它是方向的从属状态，切走就没有意义了） */
  const onModeChange = (m: EntryMode): void => {
    /*
      🔴 批 `119`：**退款态不许切方向 / 切形态**。

      退款是"钱退回来"⇒ 形态恒为「单笔 `in` 腿 + 挂原**支出**分类」（内核 `isRefundTxn`）。
      切到支出 / 转账 / 工资条后 `parentId` 还在草稿里 ⇒ 提交时服务端 `bindRefundToParent()`
      必 400（"`parentId` 只能用于退款"）。与其让用户走到那一步，不如当场说清为什么不给切。
    */
    if (refund !== null && m !== 'in') {
      setErrors([
        '这是一笔退款：方向锁定「收入」（钱退回原账户），不能改成支出 / 转账 / 工资条。要记别的收支请先离开本页。',
      ]);
      return;
    }
    setMode(m);
    setErrors([]);
    // 从收 / 支切到别的模式 ⇒ 拆分开关必须关掉（工资条有自己的多笔语义，不复用这个开关）
    const nextSplit = supportsSplit(m) ? split : false;
    setSplit(nextSplit);
    setDraft((d) => applyMode(d, m, nextSplit));
    /**
     * 🆕 批 `141`：第一次进「工资条」⇒ 按常见结构铺一份清单（`DR-016` §3.8① 的起点）。
     * 只是起点：每条都能删、能改名、能改金额；空白金额的项**不记**。
     */
    if (m === 'salary' && salaryItems === null) {
      setSalaryItems(defaultSalaryItems(accounts.data?.items ?? [], catItems));
    }
  };

  /**
   * 开 / 关「拆分」。
   *
   * 🔴 打开时由 `applyMode` 把**所有子项方向对齐成父方向** ——
   * 这就是负责人那条硬约束（「必须是一个方向」）的落地点，
   * 也是 `checkFundSingleOutlet`（资金出口唯一）在端侧不重复实现的前提：
   * 端侧**不判定**，而是**不给出违反的机会**。
   */
  const onSplitChange = (on: boolean): void => {
    setSplit(on);
    setErrors([]);
    setDraft((d) => {
      const next = applyMode(d, mode, on);
      if (!on) return next;
      // 🔴 打开拆分 ⇒ 账户统一为**第一笔**（= 父级）——
      //    否则从单笔切进来、或编辑历史数据时，跨账户的子笔会在"不显示账户"的新界面下
      //    **静默保留不一致**（`DR-040` D7 预告过这条）。统一动作只发生在这里与父级选择器上。
      const parent = next.legs[0]?.accountId ?? '';
      return { ...next, legs: next.legs.map((l) => ({ ...l, accountId: parent })) };
    });
  };

  const submit = (): void => {
    setOk(null);
    setDeletedConflict(false);
    /**
     * 🆕 批 `141`：工资条先把**子项**摊成腿（`expandSalaryItems` 是唯一展开处），
     * 再走**同一个** `buildRequest` —— 形态校验仍由共享层内核判，端侧不重写规则。
     *
     * 🔴 校验顺序：先报"用户能改的事"（缺分类 / 缺目标账户 / 腿数超限），
     * 再让内核判形态。否则用户会先看到一句「跨账户的多腿交易上限为 15 条」，
     * 却不知道自己只填了 8 项（转账一项占 2 腿）。
     */
    if (mode === 'salary' && salaryItems !== null) {
      const issues = validateSalaryItems(salaryItems, salaryMain);
      if (issues.length > 0) {
        setErrors(issues);
        return;
      }
    }
    const submitDraft =
      mode === 'salary' && salaryLegs !== null ? { ...draft, legs: salaryLegs } : draft;
    // 🔴 账户池传**全量**：工资条的腿会落在社保 / 基金账户上，日常池里没有它们
    // 🔴 `editing` 只用于一件事：`PATCH` 里"清掉简化记账标记"必须显式发 `null`（批 141）
    const r = buildRequest(submitDraft, { accounts: allOptions, editing });
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors([]);
    // 🔴 编辑态传 id ⇒ 走 `PATCH`（同一次构建结果，防两套拼装 —— 铁律 24）
    save.mutate({ id: editing ? editOf : null, req: r.request });
  };

  /**
   * 🆕 「只记到手这一笔」= `DR-016` §3.6 的**简化模式**。
   *
   * 切到普通「收入」单笔：金额取「银行到账」（没填就取清单合计），分类预填「工资」。
   *
   * ⚠️ **诚实登记**：§3.6 要求该笔标「简化记账（未含社保与个税）」，
   * 但 `transactions` 没有这个标记列 ⇒ 端侧写不进去，年度税负页也就**不会**自动提示
   * 「数据不完整」。本批不新造字段（schema 不做顺手加），已在 changelog 登记为待办。
   */
  const toSimpleSalary = (): void => {
    const hint = salaryNetText.trim();
    const totals = salaryItems === null ? null : salaryTotals(salaryItems);
    const amountText =
      hint !== ''
        ? hint
        : totals !== null && totals.netCents > 0
          ? centsToYuanText(toCents(totals.netCents))
          : '';
    const salaryCat = catItems.some((c) => c.id === CAT.SALARY) ? CAT.SALARY : null;
    setMode('in');
    setSplit(false);
    setErrors([]);
    setDraft((d) => ({
      ...applyMode(d, 'in', false),
      /**
       * 🆕 批 141：落「简化记账」标记（`DR-016` §3.6 / C 域 §5.2.1⑤）。
       * 🔴 两种口径在腿上同形（净额都等于实发）⇒ 不标就再也分不出来，
       *    报表「数据不完整」的提示也就没有判据。
       */
      salaryMode: SALARY_MODE_SIMPLE,
      legs: [
        { accountId: salaryMain, direction: LegDirection.In, amountText, categoryId: salaryCat },
      ],
    }));
  };

  /**
   * 🆕 批 141 · **沿用上月**：取最近一笔完整工资条，折回子项，连金额一起带出。
   *
   * ## 三条判断
   *
   * 1. **金额一起带**（不是只带结构）：工资条每月几乎一样，只带结构等于让用户重填 7 个数。
   *    兜底是顶部那条**对账徽标** —— 带错了（这月涨薪/扣款变了）差额会当场标出来。
   * 2. **到账金额也预填**（= 上月实发）：否则一进来就是"差 ¥x 未归类"，看着像出错了。
   * 3. **折不了就明确说**（不复用半成品）：`salaryItemsFromLegs()` 返回 `null` 说明上月那笔结构
   *    与子项对不上，此时**不填**任何东西，只给一句提示 —— 填一半比不填更危险。
   */
  const copyLastSalary = async (): Promise<void> => {
    setSalaryCopyBusy(true);
    setErrors([]);
    setOk(null);
    try {
      const res = await getLastSalary();
      if (res.item === null) {
        setErrors(['这个账本还没有可沿用的工资条 —— 先完整记一次，下个月就能一键带出']);
        return;
      }
      const folded = salaryItemsFromLegs(res.item.legs, {
        accounts: accounts.data?.items ?? [],
        categories: catItems,
      });
      if (folded === null) {
        setErrors([`${res.item.date} 那笔工资条的结构没能识别（可能是历史数据），请手动填这次`]);
        return;
      }
      setSalaryItems(folded);
      const lastMain = mainAccountOfLegs(res.item.legs);
      if (lastMain !== null && lastMain !== '') setSalaryMainPicked(lastMain);
      const totals = salaryTotals(folded);
      if (totals.netCents > 0) {
        setSalaryNetText(centsToYuanText(toCents(totals.netCents)));
      }
      setOk(`已带出 ${res.item.date} 的工资条（${String(folded.length)} 项，金额也带上了）—— 改掉变动的数就行`);
    } catch (e) {
      setErrors([e instanceof ApiError ? e.message : String(e)]);
    } finally {
      setSalaryCopyBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {editing ? '改一笔' : refund === null ? '记一笔' : '记一笔退款'}
      </Typography>

      {/*
        🔴 **编辑提示条**（`AC-C1.41`）：编辑与新建长得几乎一样 ⇒ 必须说清"你在改一笔已有的"，
        并点明保存后的去向（回流水列表）。不写这句，用户会以为自己在记一笔新的。
      */}
      {editing && (
        <Alert severity="info" data-testid="edit-banner">
          正在修改一笔已有流水（{editOf}）—— 保存后回到
          <b>{returnTo === '/journal' ? '流水列表' : '那张卡的账单页（同一期）'}</b>。
          金额填的是<b>实付</b>；账户 / 日期改了会一起生效。
        </Alert>
      )}

      {/*
        🔴 **退款提示条**（`DR-035` v1.1 · C 域 PRD §5.2.3）。
        它必须**显眼且不可忽略**：退款与普通收入在界面上只差"挂没挂 parentId"，
        而挂不挂**不影响任何计算** ⇒ 用户看不出来自己记的是退款还是收入。
        所以这里把"冲减的是哪一笔"明说出来（原备注 + 当天日期），
        并提示方向已锁定 —— 免得用户以为"怎么自动选成收入了"。
      */}
      {refund !== null && (
        <Alert severity="info" data-testid="refund-banner">
          正在冲减：{refund.note === null || refund.note === '' ? '（无备注）' : refund.note}
          {' · '}
          {refund.date}
          <br />
          {/*
            🆕 批 `129`：金额栏的默认值改了（空串 → 剩余可退），这里必须说明，
            否则用户会以为"系统凭什么帮我填了钱"。同时说清它是**默认值不是上限**。
          */}
          金额已按<b>剩余可退</b>填好（<b>可改小</b>）；支持多次部分退款；方向已按「收入」锁定 ——
          退款在账本里就是一条进账，冲减掉原来的支出（DR-016）。
          <br />
          {/*
            🔴 批 `119`：分类这格最容易踩空 —— 方向是「收入」，但要选的是**原来的支出分类**。
            不写这句，用户会在"收入分类列表"里找不到要退的那一类（负责人 2026-10-29 报的就是这个）。
          */}
          <b>分类请选原来的支出分类</b>（已按原交易预填，可改）——
          退款靠「收入 + 支出分类」才被认成冲减，挂收入分类会被当成一笔普通收入。
          {/*
            🆕 批 `120`（`DR-069` D6）：**部分退款后必须告诉用户还能退多少**。
            只在"已经退过"时出现 —— 第一次退款时原额就是可用额，多说一句是噪音。
          */}
          {refund.refundedAmount > 0 && refundLeftCents > 0 && (
            <>
              <br />
              <b>最多可退 {formatMoneyText(toCents(refundLeftCents), singleCurrency)}</b>
              （原 {formatMoneyText(toCents(refundOriginalCents), singleCurrency)}，已退{' '}
              {formatMoneyText(toCents(refund.refundedAmount), singleCurrency)}）
            </>
          )}
        </Alert>
      )}

      {/*
        🆕 网格化（㊱）：**主面板 + 300px 侧栏**，列宽照抄设计的 `.cmp`
        （`03-记一笔_原型.html:232` 的 `.cmp { grid-template-columns: minmax(0,1fr) 300px }`）。
        🔴 侧栏放的是**本页真实存在的规则说明**（形态 / 腿数上限 / 分类可空），
        不是设计稿里的宣传文案 —— 原型侧栏写的是「≤5 秒完成」这类未实现的承诺，
        搬进产品就是把广告当功能。
        ⚠️ 侧栏是**固定 300px**：它装的是短说明，不需要随视口缩放；
        窄屏由下面的 `≤1080px` 单列兜底（照抄设计同名断点）。
      */}
      <Grid templateColumns="minmax(0, 1fr) 300px" sx={{ alignItems: 'start', '@media (max-width: 1080px)': { gridTemplateColumns: 'minmax(0, 1fr)' } }}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {/*
              ── 模式选择（🔴 2026-10-04 第二次重分组：拆分收回为**开关**）──

              | 同级按钮 | 用户在想什么 |
              |---|---|
              | 支出 | "我花了一笔钱" |
              | 收入 | "我进了一笔钱" |
              | 转账 | "我把钱从一个账户挪到另一个" |
              | 工资条 | "这是我这个月工资，扣了这些" |

              🔴 **「拆分」不再是同级按钮，而是收 / 支内部的开关**（负责人 2026-10-04）：
              > 「拆分收支是银行对账单就一笔支出，但是会细分到不同类别，
              >  所以只要在支出或收入的地方做个开关，拆分就行。
              >  **但是必须是一个方向，要么支出，要么收入，因为父项在银行对账单就一笔**」

              ⇒ 拆分**从属于**某个方向（它是"这一笔要不要分几处记"），不是并列的第四种记账。
              ⇒ 打开开关后**子项方向锁死 = 父方向**，UI 不再给子项方向选择（不给犯错的机会）。

              🔴 **「工资条」必须留在同级**（负责人同日原话：「工资条另外实现，应该和收入和支出同等级的」）：
              它天然有收有付（实发 `in` + 个税/社保 `out`）⇒ **不满足"一个方向"**，
              塞进收 / 支的拆分开关里会直接违反上面那条硬约束。
            */}
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={mode}
              onChange={(_e, v: EntryMode | null) => {
                if (v !== null) onModeChange(v);
              }}
            >
              <ToggleButton value="out">支出</ToggleButton>
              <ToggleButton value="in">收入</ToggleButton>
              <ToggleButton value="transfer">转账</ToggleButton>
              <ToggleButton value="salary">工资条</ToggleButton>
            </ToggleButtonGroup>

            {/* 当前模式的**一句人话说明**（替代让用户猜"腿"是什么） */}
            <Typography variant="caption" color="text.secondary" data-testid="mode-hint">
              {MODE_HINT[mode]}
            </Typography>

            {/*
              🔴 「拆分」开关 —— 只出现在收 / 支两种日常记账里。
              它是**方向的从属状态**：对账单上仍是一笔，账本里细分到不同分类。

              🆕 批 `119`：**退款态不显示这个开关** —— 退款冲的是一笔具体支出，
              只能记成一笔（`buildRequest` 也会拦）。给了开关却拦在提交，是自找的一步错。
            */}
            {supportsSplit(mode) && refund === null && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={split}
                      onChange={(_e, v) => onSplitChange(v)}
                      data-testid="split-switch"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      这笔要分开记
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        对账单上仍是一笔{mode === 'out' ? '支出' : '收入'}，账本里细分到不同分类
                      </Typography>
                    </Typography>
                  }
                />
                {split && (
                  <Typography variant="caption" color="text.secondary" data-testid="split-hint">
                    {SPLIT_HINT[mode === 'out' ? 'out' : 'in']}
                  </Typography>
                )}
              </>
            )}

            {/* ── 转账：两个账户 + 一个金额 ── */}
            {mode === 'transfer' && (
              <>
                <AccountPicker
                  label="转出账户"
                  accounts={options}
                  value={draft.fromAccountId}
                  onChange={(id) => {
                    setDraft((d) => ({ ...d, fromAccountId: id }));
                  }}
                />
                <AccountPicker
                  label="转入账户"
                  accounts={options}
                  value={draft.toAccountId}
                  onChange={(id) => {
                    setDraft((d) => ({ ...d, toAccountId: id }));
                  }}
                  helperText="只列日常账户 · 两端币种须一致（跨币种转账属另一形态，暂未开放）"
                />
                <TextField
                  required
                  inputMode="decimal"
                  label={transferSym === '' ? '转账金额' : `转账金额（${transferSym}）`}
                  placeholder="0.00"
                  value={draft.transferAmountText}
                  onChange={(e) => setDraft((d) => ({ ...d, transferAmountText: e.target.value }))}
                  error={
                    draft.transferAmountText !== '' && !amountOk(draft.transferAmountText)
                  }
                  helperText="两端同一个数（同币种转账，转出与转入金额相等）"
                />
              </>
            )}

            {/* ── 🆕 批 `141`：工资条 = **一项一行的清单**（不是每项一张卡） ── */}
            {mode === 'salary' && salaryItems !== null && (
              <>
                <SalarySheet
                  items={salaryItems}
                  onItemsChange={setSalaryItems}
                  mainAccountId={salaryMain}
                  onMainAccountChange={setSalaryMainPicked}
                  mainAccounts={dailyOptions}
                  targetAccounts={allOptions}
                  categories={catItems}
                  netText={salaryNetText}
                  onNetTextChange={setSalaryNetText}
                  onCopyLast={() => {
                    void copyLastSalary();
                  }}
                  copyBusy={salaryCopyBusy}
                  disabled={save.isPending}
                />
                {/*
                  🔴 **只在新建时给这条退路**：编辑一笔已有的工资条点它，会把 7 条腿改成 1 条
                  （社保 / 公积金 / 个税全没了）—— 那是"改一笔"里最不该发生的事。
                */}
                {!editing && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Button size="small" onClick={toSimpleSalary} data-testid="salary-to-simple">
                      不想拆这么细？只记到手这一笔
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* 编辑态折叠失败 ⇒ 说清为什么退回逐腿，而不是给一张少了几项的表 */}
            {mode === 'salary' && salaryItems === null && editing && (
              <Alert severity="info" data-testid="salary-fallback">
                这笔工资条的结构没能自动识别（历史数据），下面按<b>原来的逐条方式</b>编辑 ——
                金额与账户都不会被改动。
              </Alert>
            )}

            {/* ── 拆分（收 / 支内多笔 · **锁同向 · 账户在父级**）/ 工资条回退（逐腿） ── */}
            {isMultiMode(mode, split) && !(mode === 'salary' && salaryItems !== null) && (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  {mode === 'salary'
                    ? '这个月工资的构成'
                    : mode === 'out'
                      ? '这笔钱花在了这几处'
                      : '这笔钱来自这几处'}
                </Typography>

                {/*
                  🔴 拆分：**账户只在父级选一次**（`DR-040` D7）。
                  子笔行不再出现账户 ⇒「拆分 = 同账户同向」是结构保证；
                  改这里会**同步全部子笔**（不再有"逐行改账户"的入口）。
                */}
                {mode !== 'salary' && (
                  <AccountPicker
                    label={mode === 'out' ? '支出账户（下面每一笔都用它）' : '收入账户（下面每一笔都用它）'}
                    accounts={options}
                    value={draft.legs[0]?.accountId ?? ''}
                    onChange={setSplitAccount}
                    helperText="只列日常账户；社保账户请在「账户」页展开后查看"
                  />
                )}

                {/*
                  🆕 **整单优惠**（批次 `94` · `DR-040` §4.2 / C 域 PRD 🆕9）。

                  🔴 只对**支出**出现：优惠是"减免"，收入侧没有语义（`AC-C2.34`）。
                  🔴 它是**录入助手**，不落库 —— 提交前按各行原价比例分摊进每条腿的 `discount`
                     （Σ 逐分恒等，`allocate()` 最大余数法）。
                  🔴 与行内「优惠」并存（负责人 2026-10-21 拍板）：最终 = 自填 + 分摊，
                     合流点只有 `finalDiscounts()` 一处。
                */}
                {mode === 'out' && (
                  <TextField
                    inputMode="decimal"
                    label={splitSym === '' ? '整单优惠（选填）' : `整单优惠（${splitSym}，选填）`}
                    placeholder="0.00"
                    value={draft.splitDiscountText ?? ''}
                    onChange={(e) => {
                      setDraft((d) => ({ ...d, splitDiscountText: e.target.value }));
                    }}
                    error={(draft.splitDiscountText ?? '').trim() !== '' && !wholeResolved.ok}
                    helperText={
                      (draft.splitDiscountText ?? '').trim() === ''
                        ? '这一单整体减了多少（满减 / 抹零）—— 会按各行原价比例分摊到下面每一笔'
                        : wholeResolved.ok
                          ? `共减 ${splitSym}${centsToYuanText(toCents(wholeResolved.total))}，已按原价比例摊到各行（分摊之和 = 总额，逐分闭合）`
                          : (wholeResolved.errors[0] ?? '')
                    }
                    slotProps={{ htmlInput: { 'data-testid': 'split-whole-discount' } }}
                  />
                )}

                {/*
                  🆕 批 `110`：**区块级**统一给一次分类说明。
                  🔴 行内 `CategoryPicker` 传的是 `showHint={false}`（否则每行多一行字、
                  把「一行 4 列」撑成两行，负责人 2026-10-26 截图里的"不整齐"）——
                  那句「缩进的是二级细分」于是没地方说了，补在这里，**只在区块出现一次**。
                */}
                <Typography variant="caption" color="text.secondary" data-testid="split-cat-hint">
                  分类：缩进的是二级细分，选一级 = 记在大类；可键入拼音或汉字搜。
                </Typography>

                {draft.legs.map((leg, i) => (
                  <LegRow
                    key={i}
                    index={i}
                    leg={leg}
                    accounts={options}
                    items={catItems}
                    removable={draft.legs.length > 1}
                    // 🔴 拆分锁同向 ⇒ **不给方向选择**（工资条仍可跨向，保留方向开关）
                    lockDirection={mode !== 'salary'}
                    // 🔴 拆分 ⇒ **紧凑单行**（分类 / 金额 / 优惠 / 备注），无账户、无「不计收支」
                    isSplit={mode !== 'salary'}
                    // 本行分摊到的整单优惠（只读尾巴）—— 数字来自上面那次唯一分摊
                    wholeShare={wholeResolved.shares[i] ?? 0}
                    onChange={(patch) => patchLeg(i, patch)}
                    onRemove={() => removeLeg(i)}
                  />
                ))}
                {/* 🔴 按钮文案按模式区分：拆分是"再加一处"，工资条是"再加一项扣款" */}
                <Button
                  variant="outlined"
                  onClick={addLeg}
                  disabled={draft.legs.length >= legCap}
                  data-testid="add-leg-button"
                >
                  {mode === 'salary' ? '+ 再加一项（扣款 / 补贴）' : '+ 再加一处'}
                </Button>
                {/*
                  ⚠️ 条数上限**平时不展示**（那是实现细节）；只有真撞到上限（按钮 disabled）才提示。
                  上限按**当前形态**取，且常量来自共享层（规格 §3.5：端侧禁自算档位）。
                */}
                {draft.legs.length >= legCap && (
                  <Typography variant="caption" color="warning.main">
                    {mode === 'salary'
                      ? `已达这笔交易可记的条数上限（${String(MaxPayrollItems)} 条）`
                      : `已达同一账户拆分的上限（${String(MaxSplitChildren)} 笔）—— 跨账户的多笔请改用「工资条」`}
                  </Typography>
                )}
                {mode !== 'salary' && (
                  <Typography variant="caption" color="text.secondary" data-testid="split-cols-hint">
                    每行填：分类 · <b>实付</b> · 优惠 · 备注 —— 账户由上面这项统一决定；
                    <b>「实付」就是你实际付出去的、要入账的那个数</b>（余额 / 统计 / 预算都只认它）；
                    优惠是<b>减免</b>（钱从未流出），优惠前的原价 = 实付 + 优惠，<b>只作参考、不参与记账</b>；
                    整单优惠会按<b>各行原价比例</b>自动摊进每一笔（分摊之和恒等于总额）。
                  </Typography>
                )}
              </>
            )}

            {/* ── 支出 / 收入：单笔 ── */}
            {(mode === 'out' || mode === 'in') && !split && (
              <>
                {/*
                  🔴 批 `117`：label 由「金额」改为「**实付金额**」—— 这一格填什么就入什么账，
                  不能让用户以为自己填的是账单原价（优惠会另算）。
                */}
                <TextField
                  required
                  inputMode="decimal"
                  label={singleSym === '' ? '实付金额' : `实付金额（${singleSym}）`}
                  placeholder="0.00"
                  value={draft.legs[0]?.amountText ?? ''}
                  onChange={(e) => patchLeg(0, { amountText: e.target.value })}
                  error={
                    (draft.legs[0]?.amountText ?? '') !== '' &&
                    !amountOk(draft.legs[0]?.amountText ?? '')
                  }
                  helperText="最多两位小数（金额以「分」存储，禁浮点）"
                  /* 🆕 批 129：给"退款默认全额"一个可断言的锚点（静态渲染能读到 `input.value`） */
                  slotProps={{ htmlInput: { 'data-testid': 'single-amount' } }}
                />
                {/*
                  🆕 批 `115` · T8：**单笔支出也能填优惠**（负责人 2026-10-28 第 8 条）。

                  🔴 只 `out` 渲染：`in` 腿带优惠服务端**直接拒**（`AC-C2.34`）——
                  不给犯错的机会，而不是渲染出来再拦（与工资条那一支同手法）。
                  🔴 金额仍是**实付**（`DR-040` D3：优惠是减免，钱从未流出）；
                  原价在这行 helperText 里给，**不落库**。
                */}
                {mode === 'out' && (
                  <TextField
                    inputMode="decimal"
                    label={singleSym === '' ? '优惠（选填）' : `优惠（${singleSym}，选填）`}
                    placeholder="0.00"
                    value={singleDiscountText}
                    onChange={(e) => patchLeg(0, { discountText: e.target.value })}
                    error={singleDiscBad}
                    helperText={
                      singleDiscBad
                        ? '最多两位小数'
                        : singleOriginalCents === null || singlePaidCents === null
                          ? '按实付记；有满减 / 抹零就填这里'
                          : /*
                              🔴 批 `117`：明写「入账」是哪个数。
                              修前只写「原价 ¥65（优惠不减余额，只影响原价展示）」——
                              用户读到"只影响原价展示"，就会以为金额栏该填原价 65，
                              于是实付出 60 的一笔被记成 65（优惠前金额入账）。
                            */
                            `入账 ${singleSym}${centsToYuanText(toCents(singlePaidCents))}（原价 ${singleSym}${centsToYuanText(toCents(singleOriginalCents))}；优惠只作记录，不入账）`
                    }
                    inputProps={{ 'data-testid': 'txn-single-discount' }}
                  />
                )}
                <AccountPicker
                  label="账户"
                  accounts={options}
                  value={singleAcc}
                  onChange={(id) => {
                    patchLeg(0, { accountId: id });
                    /**
                     * 🔴 切到**非信用卡** ⇒ 必须清掉分期三字段（`DR-067`）。
                     *
                     * 不清的后果是本批最忌的那类"静默生效"：开关因为 `showInstallment` 变假而
                     * **从界面上消失**，但 `installmentPeriods` 还在草稿里 ⇒ 提交后服务端 400
                     * 「分期只能挂在信用卡账户上」，而用户在屏幕上找不到任何"我要分期"的痕迹。
                     */
                    if (options.find((a) => a.id === id)?.type !== AccountType.Credit) {
                      onInstallmentChange(false);
                    }
                  }}
                  helperText="只列日常账户；社保账户请在「账户」页展开后查看"
                />

                {/*
                  🆕 批 `116`（`DR-067`）：**记账时信用卡直接分期**。

                  🔴 开关默认关（不看 = 与今天完全一致）。打开后本金**不由用户填** ——
                  本金恒等于本笔金额，首期账单日 = 该卡下一个账单日，两者都由服务端定。
                  用户少填一个数，就少一处「本金 ≠ 消费额」的对账缺口。
                */}
                {showInstallment && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={installmentOn}
                          onChange={(_e, v) => onInstallmentChange(v)}
                          data-testid="installment-switch"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          这笔分 N 期
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            和这笔消费一起记账，不用事后到分期页再办一遍
                          </Typography>
                        </Typography>
                      }
                    />
                    {installmentOn && (
                      <>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          {/* 期数取值与分期页**同一组**（`InstallmentsPage` 的 3/6/9/12/18/24/36） */}
                          <TextField
                            select
                            size="small"
                            label="期数"
                            value={String(draft.installmentPeriods ?? 12)}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, installmentPeriods: Number(e.target.value) }))
                            }
                            inputProps={{ 'data-testid': 'installment-periods' }}
                            sx={{ width: 132 }}
                          >
                            {INSTALLMENT_PERIOD_OPTIONS.map((n) => (
                              <MenuItem key={n} value={String(n)}>
                                {n} 期
                              </MenuItem>
                            ))}
                          </TextField>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={draft.installmentFeeMode ?? 'total'}
                            onChange={(_e, v: string | null) => {
                              if (v === null) return;
                              setDraft((d) => ({
                                ...d,
                                installmentFeeMode: v === 'per_period' ? 'per_period' : 'total',
                              }));
                            }}
                            data-testid="installment-fee-mode"
                          >
                            <ToggleButton value="total">{INSTALLMENT_FEE_MODE_LABELS.total}</ToggleButton>
                            <ToggleButton value="per_period">
                              {INSTALLMENT_FEE_MODE_LABELS.per_period}
                            </ToggleButton>
                          </ToggleButtonGroup>
                          <TextField
                            size="small"
                            inputMode="decimal"
                            label={
                              (draft.installmentFeeMode ?? 'total') === 'total'
                                ? `总手续费（${singleSym || '元'}，0 = 免息）`
                                : `每期手续费（${singleSym || '元'}，0 = 免息）`
                            }
                            placeholder="0.00"
                            value={draft.installmentFeeText ?? ''}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, installmentFeeText: e.target.value }))
                            }
                            error={
                              (draft.installmentFeeText ?? '').trim() !== '' &&
                              !amountOk(draft.installmentFeeText ?? '')
                            }
                            inputProps={{ 'data-testid': 'installment-fee' }}
                            sx={{ flexGrow: 1, minWidth: 180 }}
                          />
                        </Stack>
                        {singleBillingDay === null ? (
                          // 没有账期就没有"本期账单"（E9）—— 与其让用户提交后吃 400，不如现在说清
                          <Alert severity="warning" data-testid="installment-no-billing">
                            这张卡还没配账单日 / 还款日，无法分期 —— 请先到「账户」页补齐账期。
                          </Alert>
                        ) : (
                          <Typography variant="caption" color="text.secondary" data-testid="installment-hint">
                            本金 = 本笔金额
                            {/*
                              🔴 金额还没填（或格式不对）时**不显示 `¥0.00`** —— 那会让用户以为
                              "本金是 0"。宁可只说"= 本笔金额"，等金额填对了再带上数字。
                            */}
                            {installmentAmountCents === null
                              ? ''
                              : ` ${singleSym}${centsToYuanText(toCents(installmentAmountCents))}`}
                            {' · '}
                            首期账单日 {startBillDayHint ?? '（由服务端确定）'}
                            {'（由服务端最终确认）'}
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                )}

                {cats.isPending ? null : cats.isError ? (
                  <Alert severity="warning">
                    分类读取失败：{String(cats.error)}（可先不选分类直接记）
                  </Alert>
                ) : (
                  <CategoryPicker
                    /*
                      🔴 批 `119`：**退款态必须列支出分类**（负责人 2026-10-29 反馈
                      「退款无法退款…点击退款是记为收入，约定退回原来的分类，
                      但是收入没有原来支出的分类，故报错」）。

                      退款腿方向是 `in`（钱回来），但内核 `isRefundTxn()` 的判据是
                      「**`in` 腿 + 至少一条挂支出分类**」—— 按方向筛成收入分类，两条路都是 400：
                      原支出分类不在列表里（选不到）· 留空或选收入分类（判不成退款）。
                    */
                    kind={
                      draft.refundParentId !== null && draft.refundParentId !== undefined
                        ? CategoryKind.Expense
                        : modeKind(mode) === 'expense'
                          ? CategoryKind.Expense
                          : CategoryKind.Income
                    }
                    label={
                      draft.refundParentId !== null && draft.refundParentId !== undefined
                        ? '冲减的分类（原来的支出分类）'
                        : '分类'
                    }
                    items={cats.data.items}
                    value={draft.legs[0]?.categoryId ?? null}
                    onChange={(v) => patchLeg(0, { categoryId: v })}
                    /*
                      🆕 批 `112-A`：**单笔表单**是分类管理的正门（`DR-062` D1 · `DR-020` B6）。
                      🔴 只在这一处接 —— 拆分行的行内组件（每行一颗按钮会乱）与批量面板都不接。
                    */
                    manageable
                  />
                )}

                {/* 🆕 垫付到…（`AC-Q3.2`）—— 只在**支出单笔**出现：
                    收入不是垫付；拆分的每一笔该各自记（见 `txn-draft.ts` 的 `advanceAccountId` 说明）。
                    选中的账户若一个都没有，就整块不出现 —— 不画灰项（㊱ 批的取向）。 */}
                {mode === 'out' && advanceOptions.length > 0 && (
                  <TextField
                    select
                    label="垫付到…（可选）"
                    value={draft.advanceAccountId ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, advanceAccountId: e.target.value }))}
                    inputProps={{ 'data-testid': 'txn-advance' }}
                    helperText="替公司 / 家人垫的钱：同时给该往来账户记一条应收（本金往来不计收支）"
                  >
                    <MenuItem value="">不垫付（普通支出）</MenuItem>
                    {advanceOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}（{o.ownerName}）
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}

            {/* ── 公共字段 ── */}
            <TextField
              label="备注"
              value={draft.note}
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            />
            <TextField
              label="日期"
              type="date"
              value={draft.date}
              onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />

            {/* 🔴 记账口径由**服务端派生**，不由上面的 tab 决定 —— 人从表单上看不出自己录的是拆分还是工资条 */}
            <Alert severity="info" icon={false} sx={{ py: 0 }}>
              <Typography variant="caption">
                记账口径：
                {/* 🆕 批 141：工资条读**展开后的腿**（`draft.legs` 在清单界面下不是真相） */}
                {describeShape(salaryLegs !== null ? { ...draft, legs: salaryLegs } : draft)}
              </Typography>
            </Alert>

            {errors.length > 0 && (
              <Alert severity="error">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {/* 🔴 已删除的流水不可编辑（`AC-C1.46`）—— 给**下一步动作**，不只给一句抱怨 */}
                {deletedConflict && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      data-testid="goto-recycle"
                      onClick={() => {
                        navigate(`/journal?statuses=${TxnStatusFilter.Deleted}`);
                      }}
                    >
                      去「已删除」里恢复后再改
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            {ok !== null && <Alert severity="success">{ok}</Alert>}

            <Button variant="contained" size="large" disabled={save.isPending} onClick={submit}>
              {save.isPending ? '保存中…' : editing ? '保存修改' : '保存'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

        {/* ── 侧栏：这一页的规则（不是宣传语） ── */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              这一页的规则
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              一笔账最多能记几笔、钱从哪个账户出，这些限制<b>由服务端判定</b>；
              本页把内容原样送过去，拦下时把它给的说明原样显示。
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              分类可以留空（「先记后分类」）；想换分类的话，在流水列表里随时能补。
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              上面四个按钮只是<b>帮你少填几步</b> —— 账本最终认的是你填的内容，
              同一笔账用哪个按钮进来，记出来都一样。
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              快捷键：<b>N</b> 从任何页面回到本页。
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Stack>
  );
}
